import errorLogger, {
  BUGSNAG_STATUSES,
} from '@picsart/growth-rc/services/errorLogger';

import canvasImageToFile from '@/utils/canvas/canvasImageToFile';
import uploadCanvasImage from '@/utils/canvas/uploadCanvasImage';
import sendUploadAnalytics from '@/utils/analytics/sendUploadAnalytics';
import type { CanvasImage } from '@/utils/canvas/canvasUtils';
import type { IImageInput, IVideoInput } from '@/types';

type UpdateInputFn = (params: {
  id: string;
  value: Partial<IImageInput | IVideoInput>;
}) => void;

type UploadAndUpdateInputParams = {
  inputId: string;
  image: CanvasImage;
  updateInput: UpdateInputFn;
  isAuthorized: boolean | undefined;
  addPendingUpload: (upload: { id: string; file: File }) => void;
  trackUpload?: boolean;
};

/**
 * Wire a canvas image into a media input. Mirrors the device-upload flow:
 *   - Logged out: show the base64 preview immediately and park the image as a
 *     pending upload. `usePendingUploads` uploads it to CDN after login, before
 *     submit — and `isFormValid` rejects the `data:` preview so it can never be
 *     submitted to the webhook raw.
 *   - Logged in: show the preview immediately (spinner overlay) and upload to
 *     CDN now, swapping `src` to the persisted URL.
 */
const uploadAndUpdateInput = async ({
  inputId,
  image,
  updateInput,
  isAuthorized,
  addPendingUpload,
  trackUpload = false,
}: UploadAndUpdateInputParams): Promise<void> => {
  if (!isAuthorized) {
    updateInput({
      id: inputId,
      value: {
        src: image.url,
        isLoading: false,
        hasUploadError: false,
      } as Partial<IImageInput | IVideoInput>,
    });
    const file = await canvasImageToFile({ url: image.url, imageId: image.id });
    if (file) {
      addPendingUpload({ id: inputId, file });
    }
    if (trackUpload) sendUploadAnalytics();
    return;
  }

  // Show the rendered canvas image immediately; keep the spinner until the CDN
  // URL lands so a premature Generate can't submit the temporary URL.
  updateInput({
    id: inputId,
    value: { src: image.url, isLoading: true } as Partial<
      IImageInput | IVideoInput
    >,
  });

  try {
    const cdnUrl = await uploadCanvasImage({
      url: image.url,
      imageId: image.id,
    });
    updateInput({
      id: inputId,
      value: { src: cdnUrl ?? image.url, isLoading: false } as Partial<
        IImageInput | IVideoInput
      >,
    });
    if (trackUpload) sendUploadAnalytics();
  } catch (error) {
    errorLogger.trigger(error as Error, BUGSNAG_STATUSES.WARN);
    // Fall back to the preview so the image is still visible.
    updateInput({
      id: inputId,
      value: { src: image.url, isLoading: false } as Partial<
        IImageInput | IVideoInput
      >,
    });
  }
};

export default uploadAndUpdateInput;
